import { createContext, type FC, useContext, useMemo, useState } from "react";

export type FileStatusContext = {
	isFileStatusPending: boolean;
};

export type FileStatusDispatchContext = {
	setIsFileStatusPending: (isPending: boolean) => void;
};

const defaultContext: FileStatusContext = {
	isFileStatusPending: false,
};

const defaultDispatchContext: FileStatusDispatchContext = {
	setIsFileStatusPending: () => {},
};

export const FileStatusContext = createContext<FileStatusContext>(defaultContext);
export const FileStatusDispatchContext =
	createContext<FileStatusDispatchContext>(defaultDispatchContext);

export type FileStatusProviderProps = {
	children: React.ReactNode;
};

export const FileStatusProvider: FC<FileStatusProviderProps> = ({ children }) => {
	const [pendingCount, setPendingCount] = useState(0);

	const contextValue = useMemo(
		() => ({
			isFileStatusPending: pendingCount > 0,
		}),
		[pendingCount],
	);

	const dispatchValue = useMemo(
		() => ({
			setIsFileStatusPending: (isPending: boolean) => {
				setPendingCount((prevCount) => {
					if (isPending) {
						return prevCount + 1;
					} else {
						return Math.max(0, prevCount - 1);
					}
				});
			},
		}),
		[setPendingCount],
	);

	return (
		<FileStatusContext.Provider value={contextValue}>
			<FileStatusDispatchContext.Provider value={dispatchValue}>
				{children}
			</FileStatusDispatchContext.Provider>
		</FileStatusContext.Provider>
	);
};

export const useFileStatus = () => useContext(FileStatusContext);
export const useFileStatusDispatch = () => useContext(FileStatusDispatchContext);
